import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import Logo from '../components/Logo';
import { toast } from 'sonner';
import { 
  ArrowLeft, MapPin, Clock, Store, Tag, Users, Calendar,
  Share2, Heart, CheckCircle, AlertCircle, Percent, Ticket,
  Utensils, Briefcase, ShoppingBag, Home, Gamepad2, Car
} from 'lucide-react';

// Category config
const categoryConfig = {
  "Ristorazione": { icon: Utensils, gradient: "from-orange-500 to-red-500", bg: "bg-orange-50" },
  "Servizi": { icon: Briefcase, gradient: "from-blue-500 to-indigo-500", bg: "bg-blue-50" },
  "Shopping": { icon: ShoppingBag, gradient: "from-pink-500 to-rose-500", bg: "bg-pink-50" },
  "Salute e Benessere": { icon: Heart, gradient: "from-emerald-500 to-teal-500", bg: "bg-emerald-50" },
  "Casa e Giardino": { icon: Home, gradient: "from-lime-500 to-green-500", bg: "bg-lime-50" },
  "Tempo Libero": { icon: Gamepad2, gradient: "from-purple-500 to-violet-500", bg: "bg-purple-50" },
  "Auto e Moto": { icon: Car, gradient: "from-gray-600 to-slate-700", bg: "bg-gray-50" },
};

const OfferDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedOffers, setRelatedOffers] = useState([]);
  const [savingCoupon, setSavingCoupon] = useState(false);
  const [userPointValue, setUserPointValue] = useState(0.07);


  const fetchOffer = useCallback(async () => {
    try {
      const [offerRes, allOffersRes, pointsRes] = await Promise.all([
        api.get(`/offers/${id}`),
        api.get('/offers'),
        api.get('/settings/points').catch(() => ({ data: { user_point_value: 0.07 } })),
      ]);
      setOffer(offerRes.data);
      setUserPointValue(pointsRes.data.user_point_value || 0.07);
      
      // Get related offers (same category, exclude current)
      const related = allOffersRes.data
        .filter(o => o.category === offerRes.data.category && o.id !== id)
        .slice(0, 4);
      setRelatedOffers(related);
    } catch (error) {
      console.error('Error fetching offer:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchOffer(); }, [fetchOffer]);

  const handleSaveCoupon = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    setSavingCoupon(true);
    try {
      await api.post(`/coupons/save/${id}`);
      toast.success('Punti attivati! Trovi la proposta nella tua area personale.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'attivazione dei punti');
    } finally {
      setSavingCoupon(false);
    }
  };

  const daysRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center">
        <AlertCircle className="w-16 h-16 text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700 mb-2">Proposta non trovata</h2>
        <Link to="/offers">
          <Button>Torna alle proposte</Button>
        </Link>
      </div>
    );
  }

  const config = categoryConfig[offer.category] || { icon: Tag, gradient: "from-slate-500 to-slate-600", bg: "bg-slate-50" };
  const CategoryIcon = config.icon;
  const days = daysRemaining(offer.end_date);
  const available = offer.max_uses ? offer.max_uses - offer.current_uses : null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <Logo linkTo="/" size="md" />
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon">
                <Share2 className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="icon">
                <Heart className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero Image/Gradient */}
            <div className={`relative h-72 md:h-96 rounded-2xl overflow-hidden ${offer.image_url ? 'bg-slate-200' : `bg-gradient-to-br ${config.gradient}`}`}>
              {offer.image_url && (
                <img
                  src={offer.image_url}
                  alt={offer.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}
              {/* Points Badge (hero) */}
              <div className="absolute top-6 left-6">
                <div className="bg-white rounded-2xl px-5 py-3 shadow-xl text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-emerald-700 leading-tight">{Math.round(offer.points_awarded ?? offer.discount_percentage ?? 0)} punti</div>
                  <div className="text-[10px] sm:text-xs text-slate-500 mt-0.5">su €{Math.round(offer.base_price ?? 100)} di spesa</div>
                </div>
              </div>
              
              {/* Category Icon */}
              <div className="absolute bottom-6 right-6 w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <CategoryIcon className="w-10 h-10 text-white" />
              </div>

              {/* Urgency */}
              {days <= 3 && days > 0 && (
                <div className="absolute top-6 right-6">
                  <Badge className="bg-red-500 text-white px-4 py-2 text-sm animate-pulse">
                    <Clock className="w-4 h-4 mr-2" />
                    Ultimi {days} giorni!
                  </Badge>
                </div>
              )}
            </div>

            {/* Title & Description */}
            <div>
              <Badge className={`${config.bg} text-slate-700 mb-3`}>
                {offer.category}
              </Badge>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {offer.title}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                {offer.description}
              </p>
            </div>

            {/* Details */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg text-slate-900 mb-4">Dettagli Proposta</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Percent className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Punti</p>
                      <p className="text-slate-600">Guadagni <b>{Math.round(offer.points_awarded ?? offer.discount_percentage ?? 0)} punti</b> su ogni €{Math.round(offer.base_price ?? 100)} di spesa</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Validità</p>
                      <p className="text-slate-600">
                        Dal {formatDate(offer.start_date)} al {formatDate(offer.end_date)}
                      </p>
                    </div>
                  </div>

                  {available !== null && (
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Disponibilità</p>
                        <p className="text-slate-600">{available} attivazioni ancora disponibili</p>
                      </div>
                    </div>
                  )}

                  {offer.conditions && (
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Condizioni</p>
                        <p className="text-slate-600">{offer.conditions}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* How it works */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg text-slate-900 mb-4">Come funziona</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                      <span className="text-xl font-bold text-amber-600">1</span>
                    </div>
                    <p className="font-medium text-slate-900">Attiva la proposta</p>
                    <p className="text-sm text-slate-500 mt-1">Clicca su "Ottieni"</p>
                  </div>
                  <div className="text-center p-4">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                      <span className="text-xl font-bold text-amber-600">2</span>
                    </div>
                    <p className="font-medium text-slate-900">Vai in negozio</p>
                    <p className="text-sm text-slate-500 mt-1">Mostra il tuo QR code</p>
                  </div>
                  <div className="text-center p-4">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                      <span className="text-xl font-bold text-amber-600">3</span>
                    </div>
                    <p className="font-medium text-slate-900">Accumula punti</p>
                    <p className="text-sm text-slate-500 mt-1">Conferma e ricevi i punti</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Company Card */}
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center`}>
                    <Store className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900">{offer.company_name}</h3>
                    {offer.company_city && (
                      <div className="flex items-center gap-1 text-slate-500 text-sm">
                        <MapPin className="w-4 h-4" />
                        {offer.company_city}
                      </div>
                    )}
                  </div>
                </div>

                {offer.company_address && (
                  <div className="p-4 bg-slate-50 rounded-xl mb-6">
                    <p className="text-sm text-slate-500 mb-1">Indirizzo</p>
                    <p className="text-slate-700">{offer.company_address}</p>
                  </div>
                )}

                <div className="space-y-3">
                  <Button 
                    onClick={handleSaveCoupon}
                    disabled={savingCoupon}
                    className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700" 
                    data-testid="get-offer-btn"
                  >
                    {savingCoupon ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <Ticket className="w-5 h-5 mr-2" />
                        Ottieni
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-center text-slate-500">
                    {user ? 'La proposta verrà attivata sul tuo account' : 'Accedi per attivare la proposta'}
                  </p>
                </div>

                {/* Punti Example - punti only, no € equivalent (visible only in user area) */}
                <div className="mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                  <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">Quanto guadagni</p>
                  <p className="text-sm text-emerald-900 mt-1">
                    Con <strong>€{Math.round(offer.base_price ?? 100)} di spesa</strong> ricevi <strong>{Math.round(offer.points_awarded ?? offer.discount_percentage ?? 0)} punti</strong>
                  </p>
                  <p className="text-xs text-emerald-700 mt-1">
                    I punti valgono come credito sulle spese condominiali (visibile nella tua area riservata).
                  </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-slate-200">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-emerald-700">{Math.round(offer.points_awarded ?? offer.discount_percentage ?? 0)} pt</p>
                    <p className="text-xs text-slate-500">Punti su €{Math.round(offer.base_price ?? 100)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-slate-900">{days > 0 ? days : 0}</p>
                    <p className="text-xs text-slate-500">Giorni rimasti</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Related Offers */}
        {relatedOffers.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Proposte simili</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedOffers.map((relOffer) => {
                const relConfig = categoryConfig[relOffer.category] || categoryConfig["Ristorazione"];
                return (
                  <Link key={relOffer.id} to={`/offers/${relOffer.id}`}>
                    <Card className="group overflow-hidden hover:shadow-lg transition-all cursor-pointer h-full">
                      <div className={`h-28 bg-gradient-to-br ${relConfig.gradient} flex flex-col items-center justify-center relative`}>
                        <span className="text-2xl font-bold text-white">{relOffer.discount_percentage} pt</span>
                        <span className="text-[10px] font-semibold text-white/90 tracking-wide">punti / €100</span>
                      </div>
                      <CardContent className="p-4">
                        <h4 className="font-medium text-sm line-clamp-2 group-hover:text-amber-600 mb-1">
                          {relOffer.title}
                        </h4>
                        <p className="text-xs text-slate-500">{relOffer.company_name}</p>
                        {relOffer.company_city && (
                          <div className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                            <MapPin className="w-3 h-3" />
                            {relOffer.company_city}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-10 px-4 bg-white border-t border-slate-200 mt-16">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Logo linkTo="/" size="sm" />
            <p className="text-slate-500 text-sm text-center">
              © 2024 AIAC - Associazione Italiana Amministratori di Condominio
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OfferDetail;
